import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { AuthenticationService } from '../../services/authentication.service';
import { SIDEBAR_MENU, SidebarMenuItem } from '../sidebar-menu/sidebar-menu.config';

type SidebarSearchMode = 'expanded' | 'compact';

interface SidebarSearchResult extends SidebarMenuItem {
  id: string;
  label: string;
  labelHtml: string;
  moduleLabel: string;
  sectionLabel?: string;
  score: number;
}

@Component({
  selector: 'app-sidebar-search',
  templateUrl: './sidebar-search.component.html',
  styleUrls: ['./sidebar-search.component.css']
})
export class SidebarSearchComponent implements OnChanges, OnDestroy {

  @Input() t: any;
  @Input() enabled = true;
  @Input() mobileSidebarOpen = false;
  @Input() mode: SidebarSearchMode = 'expanded';

  @Output() resultSelected = new EventEmitter<void>();
  @Output() requestMobileSidebarOpen = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChildren('resultAnchor') resultAnchors: QueryList<ElementRef<HTMLAnchorElement>>;

  public isOpen = false;
  public results: SidebarSearchResult[] = [];
  public activeIndex = 0;

  public readonly shortcutLabel =
    /Mac|iPhone|iPad|iPod/.test((typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || '')
      ? '⌘ K'
      : 'Ctrl K';

  private _term = '';
  private queryTokens: string[] = [];
  private lastFocused: HTMLElement | null = null;
  private previousBodyOverflow = '';

  // Fuente única de verdad compartida con el menú lateral (app.component.html).
  private readonly sidebarMenu = SIDEBAR_MENU;

  constructor(
    private _authSvc: AuthenticationService,
    private _host: ElementRef<HTMLElement>,
  ) {}

  public get sidebarSearchTerm(): string {
    return this._term;
  }

  public set sidebarSearchTerm(value: string) {
    this._term = value || '';
    this.buildResults();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.t && this.isOpen) {
      this.buildResults();
    }
  }

  ngOnDestroy(): void {
    if (this.isOpen) {
      this.unlockBodyScroll();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.isOpen && event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    const isSearchShortcut = (event.ctrlKey || event.metaKey)
      && !event.altKey
      && !event.shiftKey
      && event.key.toLowerCase() === 'k';

    if (!isSearchShortcut || !this.enabled) {
      return;
    }

    if (this.isOpen) {
      event.preventDefault();
      this.close();
      return;
    }

    if (!this.isVisibleSearch()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.open();
  }

  public open(): void {
    if (this.isOpen) {
      this.focusSearchInput();
      return;
    }

    this.lastFocused = document.activeElement as HTMLElement;
    this.isOpen = true;
    this._term = '';
    this.buildResults();
    this.lockBodyScroll();
    this.focusSearchInput();
  }

  public close(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this._term = '';
    this.results = [];
    this.activeIndex = 0;
    this.unlockBodyScroll();

    const toRestore = this.lastFocused;
    this.lastFocused = null;
    if (toRestore && typeof toRestore.focus === 'function') {
      toRestore.focus();
    }
  }

  public clearSidebarSearch(): void {
    this.sidebarSearchTerm = '';
    this.focusSearchInput();
  }

  public trackBySidebarSearchItem(index: number, item: SidebarSearchResult): string {
    return item.id;
  }

  public onSidebarSearchResultClick(): void {
    this.close();
    this.resultSelected.emit();
  }

  public onInputKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActive(this.results.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        this.navigateActive();
        break;
      default:
        break;
    }
  }

  private moveActive(delta: number): void {
    const total = this.results.length;
    if (total === 0) {
      return;
    }

    const next = (this.activeIndex + delta + total) % total;
    this.setActive(next);
  }

  private setActive(index: number): void {
    if (this.results.length === 0) {
      return;
    }

    this.activeIndex = Math.max(0, Math.min(index, this.results.length - 1));
    this.scrollActiveIntoView();
  }

  private navigateActive(): void {
    const anchor = this.resultAnchors?.toArray()[this.activeIndex];
    if (anchor) {
      anchor.nativeElement.click();
    }
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      const anchor = this.resultAnchors?.toArray()[this.activeIndex];
      anchor?.nativeElement.scrollIntoView({ block: 'nearest' });
    });
  }

  private buildResults(): void {
    const term = this.normalizarTexto(this._term);
    const tokens = term ? term.split(/\s+/).filter(Boolean) : [];
    this.queryTokens = this._term.trim().split(/\s+/).filter(Boolean);

    const resultados: SidebarSearchResult[] = [];

    this.sidebarMenu.forEach(modulo => {
      if (!this.tieneAccesoSidebar(modulo.permissions)) {
        return;
      }

      const moduleLabel = this.obtenerTexto(modulo.labelKey, modulo.fallbackLabel);

      modulo.sections.forEach(seccion => {
        const sectionLabel = seccion.labelKey
          ? this.obtenerTexto(seccion.labelKey, seccion.fallbackLabel || '')
          : '';

        seccion.items.forEach(item => {
          if (!this.tieneAccesoSidebar(item.permissions) || !this.tieneTodosLosPermisos(item.permissionsAll)) {
            return;
          }

          const label = this.obtenerEtiquetaSidebar(item);

          const labelN = this.normalizarTexto(label);
          const contextN = this.normalizarTexto(`${moduleLabel} ${sectionLabel}`);
          const extraN = this.normalizarTexto(`${item.route} ${(item.keywords || []).join(' ')}`);
          const haystack = `${labelN} ${contextN} ${extraN}`;

          let score = 0;

          if (tokens.length) {
            let matchesAll = true;

            for (const token of tokens) {
              if (haystack.indexOf(token) === -1) {
                matchesAll = false;
                break;
              }

              if (labelN === token) {
                score += 200;
              } else if (labelN.startsWith(token)) {
                score += 120;
              } else if (new RegExp(`\\b${this.escapeRegex(token)}`).test(labelN)) {
                score += 90;
              } else if (labelN.indexOf(token) !== -1) {
                score += 60;
              } else if (contextN.indexOf(token) !== -1) {
                score += 30;
              } else {
                score += 10;
              }
            }

            if (!matchesAll) {
              return;
            }

            if (labelN.indexOf(term) !== -1) {
              score += 40;
            }
          }

          resultados.push({
            ...item,
            id: `${modulo.id}-${item.route}`,
            label,
            labelHtml: this.resaltar(label),
            moduleLabel,
            sectionLabel,
            score,
          });
        });
      });
    });

    if (tokens.length) {
      resultados.sort((a, b) => b.score - a.score);
    }

    this.results = resultados;
    this.activeIndex = 0;
  }

  private resaltar(text: string): string {
    const escaped = this.escapeHtml(text);

    if (!this.queryTokens.length) {
      return escaped;
    }

    const pattern = this.queryTokens
      .map(token => this.escapeRegex(this.escapeHtml(token)))
      .filter(Boolean)
      .join('|');

    if (!pattern) {
      return escaped;
    }

    return escaped.replace(new RegExp(`(${pattern})`, 'gi'), '<mark>$1</mark>');
  }

  private focusSearchInput(): void {
    setTimeout(() => {
      if (!this.searchInput) {
        return;
      }

      this.searchInput.nativeElement.focus();
      this.searchInput.nativeElement.select();
    });
  }

  private lockBodyScroll(): void {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = this.previousBodyOverflow;
  }

  private isVisibleSearch(): boolean {
    const host = this._host.nativeElement;

    if (this.mode === 'compact') {
      const sidebar = host.closest('#small-sidebar') as HTMLElement;
      return !!sidebar
        && !sidebar.classList.contains('d-none')
        && window.getComputedStyle(sidebar).display !== 'none';
    }

    const sidebar = host.closest('#sidebar') as HTMLElement;
    return !!sidebar
      && sidebar.classList.contains('sidebar')
      && window.getComputedStyle(sidebar).display !== 'none';
  }

  private obtenerTexto(key: string, fallback: string): string {
    return this.t && this.t[key] ? this.t[key] : fallback;
  }

  private obtenerEtiquetaSidebar(item: SidebarMenuItem): string {
    const label = this.obtenerTexto(item.labelKey, item.fallbackLabel);

    if (!item.labelSuffixKey) {
      return label;
    }

    return `${label} - ${this.obtenerTexto(item.labelSuffixKey, item.fallbackLabelSuffix || '')}`;
  }

  private static readonly DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

  private normalizarTexto(value: string): string {
    return (value || '').toString().toLowerCase().normalize('NFD').replace(SidebarSearchComponent.DIACRITICS, '').trim();
  }

  private escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private tieneAccesoSidebar(permissions?: string[]): boolean {
    if (!permissions || permissions.length === 0) {
      return true;
    }

    return this._authSvc.tienePermiso(...permissions);
  }

  private tieneTodosLosPermisos(permissions?: string[]): boolean {
    return (permissions || []).every(permiso => this._authSvc.tienePermiso(permiso));
  }

}
